import { DashboardConfig, DashboardKpiBox } from '../../types/dashboard.type'
import * as turf from '@turf/turf'

const SMALL_AREA = turf.circle(turf.point([-77.431641, 37.51844]), 80, { steps: 128, units: 'kilometers' })
const LARGE_AREA = turf.circle(turf.point([-77.431641, 37.91844]), 115, { steps: 128, units: 'kilometers' })

const defaultKpi = [
  {
    title: 'Radar incumbent',
    values: [
      {
        title: 'Detection Probability',
        progress: 92,
        value: '92%',
        appendix: 'Target >80%',
        color: 'success',
        icon: 'Check',
      },
      {
        title: 'False Alarm Rate',
        progress: 1.5,
        value: '1.5%',
        appendix: 'Target <5%',
        color: 'success',
        icon: 'Check',
      },
    ],
  },
  {
    title: '5G Commercial',
    values: [
      {
        title: 'Throughput',
        progress: 95,
        value: '950 Mbps',
        color: 'success',
        icon: 'Check',
      },
      {
        title: 'Cells Operating',
        progress: 100,
        value: '47/47',
        color: 'success',
        icon: 'Check',
      },
    ],
  },
] as DashboardKpiBox[]

export const screenshotConfigs: {
  id: number
  name: string
  config?: DashboardConfig
  playbook?: string | number
  report?: string
}[] = [
  {
    id: 101,
    name: 'V-1.3: Stop Buzzer Active',
    config: {
      status: [
        { label: 'Status', value: 'STOP BUZZER ACTIVE', color: 'error', icon: 'Block', invert: true },
        { label: 'Active Protections', value: 'FULL', color: 'error' },
        { label: '5G Cells Active', value: '0 / 47', color: 'error' },
        { label: 'Stop Buzzer', value: 'ACTIVE', color: 'error' },
        { label: 'Authority', value: 'SB-2026-0128-001' },
        { label: 'Scope', value: 'ALL' },
        { label: 'Last Update', value: 'Feb 06, 2026 14:32:01' },
      ],
      activity: [
        {
          id: 1,
          timestamp: new Date('2026-02-06T14:32:03'),
          severity: 'INFO',
          message: 'Mitigation confirmed (47 cells)',
          color: 'warning',
        },
        {
          id: 2,
          timestamp: new Date('2026-02-06T14:32:02'),
          severity: 'INFO',
          message: 'FULL_STOP executed',
          color: 'error',
        },
      ],
      map: {
        highlight: {
          red: ['all'],
        },
      },
      kpi: [
        {
          title: 'Radar incumbent',
          values: [
            {
              title: 'Detection Probability',
              progress: 86,
              value: '86%',
              appendix: 'Target >80%',
              color: 'success',
              icon: 'Check',
            },
            {
              title: 'False Alarm Rate',
              progress: 2,
              value: '2%',
              appendix: 'Target <5%',
              color: 'success',
              icon: 'Check',
            },
          ],
        },
        {
          title: '5G Commercial',
          values: [
            {
              title: 'Throughput',
              progress: 0,
              value: '0 Mbps',
              appendix: '(reduced)',
              color: 'error',
              icon: 'Warning',
            },
            {
              title: 'Cells Operating',
              progress: 0,
              value: '0/47',
              appendix: '(reduced)',
              color: 'error',
              icon: 'Warning',
            },
          ],
        },
      ],
    },
  },
  {
    id: 102,
    name: 'V-1.4: Stop Buzzer Cleared',
    config: {
      status: [
        { label: 'Status', value: 'NORMAL', color: 'success', icon: 'CheckCircle', invert: true },
        { label: 'Active Protections', value: '0', color: 'success' },
        { label: '5G Cells Active', value: '47 / 47', color: 'success' },
        { label: 'Stop Buzzer', value: 'STANDBY' },
        { label: 'Last Update', value: 'Feb 06, 2026 14:32:01' },
      ],
      activity: [
        { id: 1, timestamp: new Date('2026-02-06T14:45:01'), severity: 'INFO', message: 'All cells restored' },
        { id: 2, timestamp: new Date('2026-02-06T14:45:00'), severity: 'INFO', message: 'Stop Buzzer cleared' },
      ],
      kpi: defaultKpi,
    },
  },
  {
    id: 103,
    name: 'V-2A.1: Radar Data Received - Pending',
    config: {
      status: [
        { label: 'Status', value: 'NORMAL', color: 'success', icon: 'CheckCircle', invert: true },
        { label: 'Pending Protection Zone', value: 'zone_radar_001', color: 'warning' },
        { label: 'Last Update', value: 'Feb 06, 2026 14:32:01' },
      ],
      activity: [
        {
          id: 1,
          timestamp: new Date('2026-02-06T14:45:00'),
          severity: '',
          message: 'Narrowband radar activity scheduled; Playbook applying Tier 2 obfuscation.',
        },
      ],
      map: {
        shapes: [
          {
            data: SMALL_AREA,
            style: {
              color: '#f90',
              weight: 2,
              dashArray: [5, 10],
            },
          },
        ],
      },
      kpi: defaultKpi,
    },
  },
  {
    id: 104,
    name: 'V-2A.2: Protection Zone Sent - Staged',
    config: {
      status: [
        { label: 'Status', value: 'PROTECTION STAGED', color: 'warning', icon: 'Warning', invert: true },
        { label: 'Staged', value: 'zone_radar_001', color: 'warning' },
        { label: 'Band', value: '3.24-3.28 GHz' },
        { label: 'Guidance', value: 'AVOID_ZONE', color: 'warning' },
        { label: 'Last Update', value: 'Feb 06, 2026 14:32:01' },
      ],
      activity: [
        { id: 1, timestamp: new Date('2026-02-06T14:45:00'), severity: '', message: 'Protection Zone sent to DSS-DF' },
        {
          id: 2,
          timestamp: new Date('2026-02-06T14:40:00'),
          severity: '',
          message: 'DSS-DF acknowledged, staging partial mitigation',
        },
      ],
      map: {
        highlight: {
          yellow: ['8594602', '8208829', '8204476', '8320187', '8658840', '8007523', '1303123', '19631', '8127533'],
        },
        shapes: [
          {
            data: SMALL_AREA,
            style: {
              color: '#f90',
              weight: 2,
            },
          },
        ],
      },
      kpi: defaultKpi,
    },
  },
  {
    id: 105,
    name: 'V-2A.3: Protection Zone Active - Partial Blanking',
    config: {
      status: [
        { label: 'Status', value: 'PROTECTION ACTIVE', color: 'warning', icon: 'Warning', invert: true },
        { label: 'Active Zone', value: 'zone_radar_001', color: 'warning' },
        { label: 'Band', value: '3.24-3.28 GHz' },
        { label: 'Remaining', value: '1:58:32' },
        { label: 'Cells Blanked', value: '8/47', color: 'warning' },
        { label: '5G Capacity', value: '83%', color: 'warning' },
        { label: 'Guidance', value: 'AVOID_ZONE', color: 'warning' },
        { label: 'Last Update', value: 'Feb 06, 2026 14:32:01' },
      ],
      activity: [
        {
          id: 1,
          timestamp: new Date('2026-02-06T14:00:01'),
          severity: '',
          message: 'Mitigation confirmed (8 cells, PRB blanking)',
        },
        { id: 2, timestamp: new Date('2026-02-06T14:00:00'), severity: '', message: 'Protection Zone active' },
      ],
      map: {
        highlight: {
          yellow: ['8594602', '8208829', '8204476', '8320187', '8658840', '8007523', '1303123', '19631', '8127533'],
        },
        shapes: [
          {
            data: SMALL_AREA,
            style: {
              color: '#f90',
              weight: 2,
            },
          },
        ],
      },
      kpi: [
        {
          title: 'Radar incumbent',
          values: [
            {
              title: 'Detection Probability',
              progress: 86,
              value: '86%',
              appendix: 'Target >80%',
              color: 'success',
              icon: 'Check',
            },
            {
              title: 'False Alarm Rate',
              progress: 2,
              value: '2%',
              appendix: 'Target <5%',
              color: 'success',
              icon: 'Check',
            },
          ],
        },
        {
          title: '5G Commercial',
          values: [
            {
              title: 'Throughput',
              progress: 62,
              value: '620 Mbps',
              appendix: '(reduced)',
              color: 'success',
            },
            {
              title: 'Cells Operating',
              progress: 75,
              value: '35/47',
              appendix: '(reduced)',
              color: 'warning',
              icon: 'Block',
            },
          ],
        },
      ],
    },
  },
  {
    id: 106,
    name: 'V-2A.4: KPI Panel - Coexistence Achieved',
    config: {
      status: [
        { label: 'Status', value: 'COEXISTENCE ACHIEVED', color: 'success', icon: 'CheckCircle', invert: true },
        { label: 'Active Zone', value: 'zone_radar_001', color: 'warning' },
        { label: 'Band', value: '3.24-3.28 GHz' },
        { label: 'Remaining', value: '1:58:32' },
        { label: 'Last Update', value: 'Feb 06, 2026 14:32:01' },
      ],
      activity: [
        {
          id: 1,
          timestamp: new Date('2026-02-06T14:00:01'),
          severity: '',
          message: 'Mitigation confirmed (8 cells, PRB blanking)',
        },
        { id: 2, timestamp: new Date('2026-02-06T14:00:00'), severity: '', message: 'Protection Zone active' },
      ],
      map: {
        highlight: {
          yellow: ['8594602', '8208829', '8204476', '8320187', '8658840', '8007523', '1303123', '19631', '8127533'],
        },
        shapes: [
          {
            data: SMALL_AREA,
            style: {
              color: '#f90',
              weight: 2,
            },
          },
        ],
      },
      kpi: defaultKpi,
    },
  },
  {
    id: 107,
    name: 'V-2A.5: Protection Zone Expired - Restored',
    config: {
      status: [
        { label: 'Status', value: 'NORMAL', color: 'success', icon: 'CheckCircle', invert: true },
        { label: 'Active Protections', value: '0', color: 'success' },
        { label: '5G Cells Active', value: '47 / 47', color: 'success' },
        { label: 'Last Update', value: 'Feb 06, 2026 14:32:01' },
      ],
      activity: [
        { id: 1, timestamp: new Date('2026-02-06T16:15:01'), severity: '', message: 'Full band restored' },
        {
          id: 2,
          timestamp: new Date('2026-02-06T16:15:00'),
          severity: '',
          message: 'Protection Zone zone_radar_001 expired',
        },
      ],
      kpi: defaultKpi,
    },
  },
  {
    id: 108,
    name: 'V-2B.1: Wideband Radar Data Received - Pending',
    config: {
      status: [
        { label: 'Status', value: 'NORMAL', color: 'success', icon: 'CheckCircle', invert: true },
        { label: 'Pending Protection Zone', value: 'zone_radar_002', color: 'error' },
        { label: 'Band', value: 'Full Band (3.20-3.40 GHz)', color: 'error' },
        { label: 'Obfuscation', value: 'Tier 3: MAXIMUM' },
        { label: 'Last Update', value: 'Feb 06, 2026 14:32:01' },
      ],
      activity: [
        {
          id: 1,
          timestamp: new Date('2026-02-06T14:45:00'),
          severity: '',
          message: 'Wideband radar activity scheduled; Playbook applying Tier 3 obfuscation.',
        },
      ],
      map: {
        shapes: [
          {
            data: LARGE_AREA,
            style: {
              color: '#f00',
              weight: 2,
              dashArray: [5, 10],
            },
          },
        ],
      },
      kpi: defaultKpi,
    },
  },
  {
    id: 109,
    name: 'V-2B.2: Protection Zone Sent - Staged (Full Vacation)',
    config: {
      status: [
        { label: 'Status', value: 'FULL BAND VACATION STAGED', color: 'error', icon: 'Block', invert: true },
        { label: 'Staged', value: 'zone_radar_002', color: 'warning' },
        { label: 'Band', value: 'Full Band (3.20-3.40 GHz)', color: 'error' },
        { label: 'Guidance', value: 'FULL_STOP', color: 'error' },
        { label: 'Last Update', value: 'Feb 06, 2026 14:32:01' },
      ],
      activity: [
        { id: 1, timestamp: new Date('2026-02-06T14:45:00'), severity: '', message: 'Protection Zone sent to DSS-DF' },
        { id: 2, timestamp: new Date('2026-02-06T14:40:00'), severity: '', message: 'DSS-DF acknowledged' },
      ],
      map: {
        highlight: {
          red: [
            '8594602',
            '8208829',
            '8204476',
            '8320187',
            '8658840',
            '8007523',
            '1303123',
            '19631',
            '8127533',
            '8600536',
            '8139474',
            '8241184',
            '6616005',
          ],
        },
        shapes: [
          {
            data: LARGE_AREA,
            style: {
              color: '#f00',
              weight: 2,
            },
          },
        ],
      },
      kpi: defaultKpi,
    },
  },
  {
    id: 110,
    name: 'V-2B.3: Full Band Vacation Active',
    config: {
      status: [
        { label: 'Status', value: 'FULL BAND VACATION ACTIVE', color: 'error', icon: 'Warning', invert: true },
        { label: 'Active Zone', value: 'zone_radar_002', color: 'error' },
        { label: 'Band', value: 'Full Band (3.20-3.40 GHz)', color: 'error' },
        { label: 'Remaining', value: '1:45:00' },
        { label: 'Cells Vacated', value: '12/47', color: 'warning' },
        { label: '5G Capacity', value: '74%', color: 'warning' },
        { label: 'Guidance', value: 'FULL_STOP', color: 'error' },
        { label: 'Last Update', value: 'Feb 06, 2026 14:32:01' },
      ],
      activity: [
        {
          id: 1,
          timestamp: new Date('2026-02-06T14:00:01'),
          severity: '',
          message: 'Full band vacation confirmed (12 cells)',
        },
      ],
      map: {
        highlight: {
          red: [
            '8594602',
            '8208829',
            '8204476',
            '8320187',
            '8658840',
            '8007523',
            '1303123',
            '19631',
            '8127533',
            '8600536',
            '8139474',
            '8241184',
            '6616005',
          ],
        },
        shapes: [
          {
            data: LARGE_AREA,
            style: {
              color: '#f00',
              weight: 2,
            },
            label: 'VACATE',
            labelColor: 'red',
          },
        ],
      },
      kpi: defaultKpi,
    },
  },
  {
    id: 111,
    name: 'V-2B.4: Full Band Vacation Expired - Restored',
    config: {
      status: [
        { label: 'Status', value: 'NORMAL', color: 'success', icon: 'CheckCircle', invert: true },
        { label: 'Active Protections', value: '0', color: 'success' },
        { label: '5G Cells Active', value: '47 / 47', color: 'success' },
        { label: 'Last Update', value: 'Feb 06, 2026 14:32:01' },
      ],
      activity: [
        { id: 1, timestamp: new Date('2026-02-06T16:15:01'), severity: '', message: 'Full band restored' },
        {
          id: 2,
          timestamp: new Date('2026-02-06T16:15:00'),
          severity: '',
          message: 'Protection Zone zone_radar_002 expired',
        },
      ],
      kpi: defaultKpi,
    },
  },
  {
    id: 112,
    name: 'V-KPI.1: Phase B - Conflict (No Protection)',
    config: {
      status: [
        { label: 'Status', value: 'RADAR MISSION COMPROMISED', color: 'error', icon: 'Warning', invert: true },
        { label: 'Active Protections', value: '0', color: 'success' },
        { label: '5G Cells Active', value: '47 / 47', color: 'success' },
        { label: 'Last Update', value: 'Feb 06, 2026 14:32:01' },
      ],
      activity: [
        { id: 1, timestamp: new Date('2026-02-06T15:35:30'), severity: 'INFO', message: 'System nominal' },
        { id: 2, timestamp: new Date('2026-02-06T15:30:30'), severity: 'INFO', message: 'Heartbeat OK (DSS-DF)' },
      ],
      kpi: [
        {
          title: 'Radar incumbent',
          values: [
            {
              title: 'Detection Probability',
              progress: 41,
              value: '41%',
              appendix: 'DEGRADED',
              color: 'error',
              icon: 'Block',
            },
            {
              title: 'False Alarm Rate',
              progress: 18,
              value: '18%',
              appendix: 'ELEVATED',
              color: 'error',
              icon: 'Block',
            },
          ],
        },
        {
          title: '5G Commercial',
          values: [
            {
              title: 'Throughput',
              progress: 95,
              value: '950 Mbps',
              appendix: '(nominal)',
              color: 'success',
              icon: 'Check',
            },
            {
              title: 'Cells Operating',
              progress: 100,
              value: '47/47',
              color: 'success',
              icon: 'Check',
            },
          ],
        },
      ],
    },
  },
  {
    id: 113,
    name: 'V-KPI.2: Phase C - Coexistence Achieved',
    config: {
      status: [
        { label: 'Status', value: 'COEXISTENCE ACHIEVED', color: 'success', icon: 'CheckCircle', invert: true },
        { label: 'Active Zone', value: 'zone_radar_001', color: 'warning' },
        { label: 'Band', value: '3.24-3.28 GHz' },
        { label: 'Remaining', value: '1:58:32' },
        { label: 'Last Update', value: 'Feb 06, 2026 14:32:01' },
      ],
      activity: [
        {
          id: 1,
          timestamp: new Date('2026-02-06T14:00:01'),
          severity: '',
          message: 'Mitigation confirmed (8 cells, PRB blanking)',
        },
        { id: 2, timestamp: new Date('2026-02-06T14:00:00'), severity: '', message: 'Protection Zone active' },
      ],
      map: {
        highlight: {
          yellow: ['8594602', '8208829', '8204476', '8320187', '8658840', '8007523', '1303123', '19631', '8127533'],
        },
        shapes: [
          {
            data: SMALL_AREA,
            style: {
              color: '#f90',
              weight: 2,
            },
          },
        ],
      },
      kpi: [
        {
          title: 'Radar incumbent',
          values: [
            {
              title: 'Detection Probability',
              progress: 86,
              value: '86%',
              appendix: 'Target >80%',
              color: 'success',
              icon: 'Check',
            },
            {
              title: 'False Alarm Rate',
              progress: 2,
              value: '2%',
              appendix: 'Target <5%',
              color: 'success',
              icon: 'Check',
            },
          ],
        },
        {
          title: '5G Commercial',
          values: [
            {
              title: 'Throughput',
              progress: 62,
              value: '620 Mbps',
              appendix: '(reduced)',
              color: 'success',
              icon: 'Check',
            },
            {
              title: 'Cells Operating',
              progress: 75,
              value: '35/47',
              color: 'success',
              icon: 'Check',
            },
          ],
        },
      ],
    },
  },
]
